import React, { ReactNode } from "react";
import { Card } from "@UI/Components/Card/Card";
import { Text } from "@UI/Components/Text/Text";
import { Flex } from "@UI/Components/Flex/Flex";
import { Box } from "@UI/Components/Box/Box";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { ReachedStatus } from "./ReachedStatus";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import { Icon } from "@UI/Components/Icon/Icon";
import { ITrackingTypes } from "../../types/serviceProvider.types";
import messages from "./messages";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useServiceProviderMetrics } from "@modules/ServiceProviders/hooks/useServiceProviderMetrics";

interface ITrackingCard {
  type: ITrackingTypes | "respects_rules";
  onClick?: (param: string) => void;
}

const formatNumber = (number: number) => {
  const formattedNumber = Math.floor(number * 10) / 10;
  const result = formattedNumber.toString().replace(/\.0$/, "");
  return result;
};

export const TrackingCard = ({ type, onClick }: ITrackingCard) => {
  const { isMobile } = useMediaQuery();
  const { currentUser } = useCurrentUser({
    includes: ["serviceProvider"],
  });

  const { data: serviceProviderTracking } = useServiceProviderMetrics(
    currentUser?.serviceProvider?.id
  );

  const tracking = serviceProviderTracking?.find(
    (element) => element.name === type
  );
  const value = tracking?.value;
  const threshold = tracking?.threshold;
  let reached;
  if (!value || !threshold) reached = false;
  else reached = value >= threshold;

  return (
    <Card
      w="full"
      p={{ base: 4, md: 5 }}
      onClick={onClick ? () => onClick(type) : undefined}
      role="group"
      cursor="pointer"
      _hover={{
        shadow: "lg",
      }}
    >
      <Flex flexDirection="row" justifyContent="space-between">
        <Box w="auto">
          <Text as="span" fontWeight={700} color="black" size="16px">
            <FormattedMessage {...messages[type]} />
            {(threshold === undefined || threshold === null) &&
            type !== "respects_rules" ? (
              <Text as="span" ml={1}>
                <FormattedMessage {...messages.to_be_defined} />
              </Text>
            ) : (
              type !== "respects_rules" && (
                <Text as="span" ml={1}>
                  <FormattedMessage
                    {...messages[`${type}_threshold`]}
                    values={{
                      threshold: threshold as ReactNode,
                    }}
                  />
                </Text>
              )
            )}
          </Text>
          <Flex mt={1} mb={1} alignItems="center">
            <Text as="span" color="gray.500" size="14px">
              <FormattedMessage {...messages.yourScoreWithDots} />
            </Text>
            <Text
              as="span"
              color={reached ? "green.500" : "gray.500"}
              fontSize={{ base: "lg", md: "lg" }}
              fontWeight={700}
              ml={2}
            >
              {type === "respects_rules" ? (
                reached ? (
                  <FormattedMessage {...messages[`value_respects_rules`]} />
                ) : (
                  <FormattedMessage
                    {...messages[`value_respects_rules_not_reached`]}
                  />
                )
              ) : !value && value !== 0 ? (
                <FormattedMessage {...messages.no_information} />
              ) : (
                <FormattedMessage
                  {...messages[`value_${type}`]}
                  values={{
                    value: formatNumber(Number(value)),
                  }}
                />
              )}
            </Text>
          </Flex>
          <ReachedStatus reached={reached} />
        </Box>
        <Box
          alignSelf={{ base: "center", md: "flex-end" }}
          cursor="pointer"
          width="auto"
        >
          {!isMobile ? (
            <Text
              as="span"
              textDecoration="underline"
              color="gray.700"
              _groupHover={{ color: "gray.800" }}
            >
              <FormattedMessage
                {...messages.seeMore}
                values={{
                  RightIcon: () => (
                    <Icon as={ArrowRight} w={4} h={4} fontWeight={700} />
                  ),
                }}
              />
            </Text>
          ) : (
            <Icon as={ArrowRight} w={4} h={4} fontWeight={700} />
          )}
        </Box>
      </Flex>
    </Card>
  );
};
